import { CSSObject } from '@emotion/react';
import { createResponsiveStyles, getSizeValue, useHoneycombTheme } from '../../theming';
import { alpha } from '../../utils';

export interface ScrollAreaStylesParams {
    scrollbarSize: number | string;
    offsetScrollbarX: boolean;
    offsetScrollbarY: boolean;
    scrollbarHovered: boolean;
    hidden: boolean;
    contained: boolean;
}

type StyleClasses = 'root' | 'viewport' | 'scrollbar' | 'thumb' | 'corner';

export function useStyles({
    scrollbarSize,
    offsetScrollbarX,
    offsetScrollbarY,
    scrollbarHovered,
    hidden,
    contained,
}: ScrollAreaStylesParams): {
    styles: { [key in StyleClasses]: CSSObject };
} {
    const { theme } = useHoneycombTheme();
    const { transitions, palette } = theme;

    const transition = `${transitions.duration.shortest}ms ${transitions.easing.easeInOut}`;

    const size = getSizeValue(scrollbarSize);

    const thumbColor = alpha(palette.common.black, 0.25);

    const root: CSSObject = {
        overflow: 'hidden',
        ...(contained
            ? createResponsiveStyles(
                  [
                      {
                          value: theme.breakpoints.contentMargins,
                          getStyles: (value: number) => {
                              const margin = getSizeValue(value * -1);
                              return {
                                  marginLeft: margin,
                                  marginRight: margin,
                              };
                          },
                      },
                  ],
                  theme
              )
            : {}),
    };

    const viewport: CSSObject = {
        width: '100%',
        height: '100%',
        paddingBottom: offsetScrollbarX ? size : undefined,
        paddingRight: offsetScrollbarY ? size : undefined,
        ...(contained
            ? createResponsiveStyles(
                  [
                      {
                          value: theme.breakpoints.contentMargins,
                          getStyles: (value: number) => {
                              const padding = getSizeValue(value);
                              return {
                                  paddingLeft: padding,
                                  paddingRight: padding,
                              };
                          },
                      },
                  ],
                  theme
              )
            : {}),
    };

    const scrollbar: CSSObject = {
        display: hidden ? 'none' : 'flex',
        userSelect: 'none',
        touchAction: 'none',
        boxSizing: 'border-box',
        transition: `background-color ${transition}, opacity ${transition}`,

        '&[data-orientation="vertical"]': {
            width: scrollbarSize,
            right: '4px',
        },

        '&[data-orientation="horizontal"]': {
            flexDirection: 'column',
            height: scrollbarSize,
        },

        '&[data-state="hidden"]': {
            opacity: 0,
        },
    };

    const thumb: CSSObject = {
        flex: 1,
        backgroundColor: thumbColor,
        borderRadius: scrollbarSize,
        position: 'relative',
        transition: `background-color ${transition}`,
        display: hidden ? 'none' : undefined,
        overflow: 'hidden',

        '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '100%',
            minWidth: `44px`,
            minHeight: `44px`,
        },
    };

    const corner: CSSObject = {
        transition: `opacity ${transition}`,
        opacity: scrollbarHovered ? 1 : 0,
        display: hidden ? 'none' : undefined,
    };

    return {
        styles: {
            root,
            viewport,
            scrollbar,
            thumb,
            corner,
        },
    };
}
